import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  Mail,
  Phone,
  Building,
  Calendar,
  Send,
  Copy,
  Check,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-amber-500' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-purple-500' },
  { value: 'completed', label: 'Completed', color: 'bg-green-500' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500' },
];

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setIsLoading(true);
        const response = await adminApi.getCustomRequest(id);
        setRequest(response.request);
        setNotes(response.request.adminNotes || '');
      } catch (err) {
        console.error('Failed to fetch request:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRequest();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      setIsSaving(true);
      await adminApi.updateCustomRequest(id, { status: newStatus });
      setRequest({ ...request, status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setIsSaving(true);
      await adminApi.updateCustomRequest(id, { adminNotes: notes });
      setRequest({ ...request, adminNotes: notes });
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-1/4"></div>
          <div className="card p-6">
            <div className="h-40 bg-bg-secondary rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto text-center">
        <p className="text-text-muted">Request not found</p>
        <button onClick={() => navigate('/requests')} className="btn-primary mt-4">
          Back to Requests
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/requests')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Requests
      </button>

      {/* Header */}
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">{request.name}</h1>
              <p className="text-text-secondary flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Submitted {formatDate(request.createdAt)}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/settings?tab=compose&to=${encodeURIComponent(request.email)}`)}
            className="btn-primary"
          >
            <Send className="w-4 h-4" />
            Email Client
          </button>
        </div>

        {/* Status Selector */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-text-muted">Status:</span>
          <div className="flex gap-2">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleStatusChange(option.value)}
                disabled={isSaving}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  request.status === option.value
                    ? `${option.color} text-white`
                    : 'bg-bg-secondary text-text-secondary hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="card p-6 mb-6">
        <h2 className="font-semibold text-text-primary mb-4">Contact Information</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-bg-secondary rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-text-muted" />
              <span className="text-text-primary">{request.email}</span>
            </div>
            <button
              onClick={() => copyToClipboard(request.email, 'email')}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              {copiedField === 'email' ? (
                <Check className="w-4 h-4 text-success" />
              ) : (
                <Copy className="w-4 h-4 text-text-muted" />
              )}
            </button>
          </div>
          {request.phone && (
            <div className="flex items-center justify-between p-3 bg-bg-secondary rounded-lg">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-text-muted" />
                <span className="text-text-primary">{request.phone}</span>
              </div>
              <button
                onClick={() => copyToClipboard(request.phone, 'phone')}
                className="p-2 hover:bg-white rounded-lg transition-colors"
              >
                {copiedField === 'phone' ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <Copy className="w-4 h-4 text-text-muted" />
                )}
              </button>
            </div>
          )}
          {request.businessName && (
            <div className="flex items-center gap-3 p-3 bg-bg-secondary rounded-lg">
              <Building className="w-5 h-5 text-text-muted" />
              <span className="text-text-primary">{request.businessName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Request Description */}
      <div className="card p-6 mb-6">
        <h2 className="font-semibold text-text-primary mb-4">Request Description</h2>
        <p className="text-text-primary whitespace-pre-wrap">{request.description}</p>
      </div>

      {/* Admin Notes */}
      <div className="card p-6">
        <h2 className="font-semibold text-text-primary mb-4">Internal Notes</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add internal notes about this request..."
          className="input min-h-[120px] resize-y"
        />
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveNotes}
            disabled={isSaving || notes === request.adminNotes}
            className="btn-primary"
          >
            {isSaving ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      </div>

    </div>
  );
}
